// Checkout Integration Example
// This demonstrates how to integrate Firebase with a website's checkout form

import { orderService, clientService, generateOrderId } from '../services/firebaseService';

/**
 * Process a checkout form submission and create order in Firebase
 * @param {Object} checkoutData - Data from the checkout form
 * @returns {Promise<Object>} - Order result with unique ID
 */
export const processCheckoutOrder = async (checkoutData) => {
  try {
    // Validate required checkout data
    if (!checkoutData.customerInfo || !checkoutData.products || !checkoutData.products.length) {
      throw new Error('Missing required checkout information');
    }

    const { customerInfo, products, shippingInfo, paymentInfo } = checkoutData;

    // 1. Check if customer already exists
    let client = await clientService.findClientByEmail(customerInfo.email);
    
    if (!client) {
      // Check by phone as backup
      client = await clientService.findClientByPhone(customerInfo.phone);
    }

    // 2. Create new client if they don't exist
    if (!client) {
      const clientData = {
        firstName: customerInfo.firstName,
        lastName: customerInfo.lastName,
        name: `${customerInfo.firstName} ${customerInfo.lastName}`,
        email: customerInfo.email,
        phone: customerInfo.phone,
        age: customerInfo.age || null,
        gender: customerInfo.gender || '',
        weight: customerInfo.weight || null,
        goalWeight: customerInfo.goalWeight || null,
        address: {
          street: shippingInfo?.address || '',
          city: shippingInfo?.city || '',
          postalCode: shippingInfo?.postalCode || ''
        },
        currentCourse: {
          productId: products[0]?.id || null,
          productName: products[0]?.name || '',
          courseStartDate: new Date().toISOString().split('T')[0],
          courseDuration: products[0]?.courseDuration || 0,
          weekCompleted: 0,
          courseDescription: products[0]?.courseDescription || ''
        },
        medicalHistory: customerInfo.medicalHistory || '',
        emergencyContact: {
          name: customerInfo.emergencyContactName || '',
          phone: customerInfo.emergencyContactPhone || '',
          relationship: customerInfo.emergencyContactRelationship || ''
        }
      };

      client = await clientService.createClient(clientData);
      console.log('New client created:', client.id);
    } else {
      console.log('Existing client found:', client.id);
    }

    // 3. Calculate order totals
    let totalAmount = 0;
    let totalCost = 0;
    const orderProducts = products.map(product => {
      const itemTotal = product.price * product.quantity;
      const itemCost = (product.costPrice || 0) * product.quantity;
      
      totalAmount += itemTotal;
      totalCost += itemCost;
      
      return {
        id: product.id,
        name: product.name,
        price: product.price,
        costPrice: product.costPrice || 0,
        quantity: product.quantity,
        courseDuration: product.courseDuration || 12, // Store course duration for progress calculation
        profit: (product.price - (product.costPrice || 0)) * product.quantity
      };
    });

    const profit = totalAmount - totalCost;

    // 4. Calculate next reorder date (use shortest cycle from products)
    const validReorderWeeks = products.map(p => p.reorderWeeks).filter(weeks => weeks && !isNaN(weeks));
    const shortestReorderWeeks = validReorderWeeks.length > 0 ? Math.min(...validReorderWeeks) : 12;
    
    const orderDate = new Date();
    const nextReorderDate = new Date(orderDate);
    
    // Validate dates before calculation
    if (isNaN(orderDate.getTime())) {
      console.warn('Invalid order date in checkout integration');
      throw new Error('Failed to generate valid order date');
    }
    
    nextReorderDate.setDate(nextReorderDate.getDate() + (shortestReorderWeeks * 7));
    
    // Validate calculated reorder date
    if (isNaN(nextReorderDate.getTime())) {
      console.warn('Failed to calculate valid next reorder date in checkout');
      throw new Error('Failed to calculate next reorder date');
    }

    // 5. Create order object
    const orderData = {
      clientId: client.id,
      clientName: `${client.firstName} ${client.lastName}`,
      phone: client.phone,
      orderDate: orderDate.toISOString().split('T')[0],
      products: orderProducts,
      totalAmount,
      totalCost,
      profit,
      status: paymentInfo?.status || 'pending', // 'pending', 'paid', 'not_paid'
      nextReorderDate: nextReorderDate.toISOString().split('T')[0],
      notes: checkoutData.notes || '',
      shippingInfo: {
        address: shippingInfo?.address || client.address?.street || '',
        city: shippingInfo?.city || client.address?.city || '',
        postalCode: shippingInfo?.postalCode || client.address?.postalCode || '',
        shippingMethod: shippingInfo?.method || 'standard',
        trackingNumber: null // Will be updated when shipped
      },
      paymentInfo: {
        method: paymentInfo?.method || 'bank_transfer',
        transactionId: paymentInfo?.transactionId || null,
        paymentDate: paymentInfo?.status === 'paid' ? new Date().toISOString() : null
      }
    };

    // 6. Create order in Firebase
    const order = await orderService.createOrder(orderData);

    console.log('Order created successfully:', {
      orderId: order.orderId,
      clientId: client.id,
      totalAmount: order.totalAmount,
      profit: order.profit
    });

    return {
      success: true,
      orderId: order.orderId,
      orderFirebaseId: order.id,
      clientId: client.id,
      totalAmount: order.totalAmount,
      message: `Order ${order.orderId} created successfully!`,
      nextReorderDate: order.nextReorderDate
    };

  } catch (error) {
    console.error('Checkout processing error:', error);
    
    return {
      success: false,
      error: error.message,
      message: 'Failed to process order. Please try again.'
    };
  }
};

/**
 * Update order status (e.g., when payment is confirmed)
 * @param {string} orderId - Firebase order ID
 * @param {string} status - New status ('paid', 'shipped', 'delivered', etc.)
 * @param {Object} additionalData - Additional data to update
 */
export const updateOrderStatus = async (orderId, status, additionalData = {}) => {
  try {
    const updateData = {
      status,
      ...additionalData
    };

    if (status === 'paid' && !additionalData.paymentDate) {
      updateData.paymentDate = new Date().toISOString();
    }

    if (status === 'shipped' && additionalData.trackingNumber) {
      updateData.shippingInfo = {
        ...additionalData.shippingInfo,
        trackingNumber: additionalData.trackingNumber,
        shippedDate: new Date().toISOString()
      };
    }

    const result = await orderService.updateOrderStatus(orderId, status, updateData);
    
    return {
      success: true,
      message: `Order status updated to ${status}`,
      data: result
    };

  } catch (error) {
    console.error('Error updating order status:', error);
    
    return {
      success: false,
      error: error.message,
      message: 'Failed to update order status'
    };
  }
};

/**
 * Example usage for a typical e-commerce checkout
 */
export const exampleCheckoutUsage = async () => {
  const checkoutFormData = {
    customerInfo: {
      firstName: 'John',
      lastName: 'Doe',
      email: 'john.doe@example.com',
      phone: '+27 12 345 6789',
      age: 35,
      gender: 'male',
      weight: 85,
      goalWeight: 75,
      medicalHistory: 'No significant medical history'
    },
    products: [
      {
        id: 'product-123',
        name: 'Semaglutide HD 10mg',
        price: 1800,
        costPrice: 850,
        quantity: 2,
        courseDuration: 16,
        reorderWeeks: 10,
        courseDescription: '4-month weight loss program'
      }
    ],
    shippingInfo: {
      address: '123 Main Street',
      city: 'Cape Town',
      postalCode: '8001',
      method: 'standard'
    },
    paymentInfo: {
      method: 'credit_card',
      status: 'paid',
      transactionId: 'txn_123456789'
    },
    notes: 'Customer prefers morning delivery'
  };

  // Process the checkout
  const result = await processCheckoutOrder(checkoutFormData);
  
  if (result.success) {
    console.log('✅ Order processed successfully:', result.orderId);
    
    // Later, update order status when shipped
    await updateOrderStatus(result.orderFirebaseId, 'shipped', {
      trackingNumber: 'TRK123456789'
    });
    
  } else {
    console.error('❌ Order processing failed:', result.error);
  }
};

// Export for use in Vue components or other parts of the application
export default {
  processCheckoutOrder,
  updateOrderStatus,
  exampleCheckoutUsage
};